/* Utility for fetch.
Please use this instead of axios or any other library for fetching anything from server,
if using a rest API */

import compareAsc from "date-fns/compareAsc";
import addMinutes from "date-fns/addMinutes";
import { CACHE_EXPIRE_IN_MINUTES } from "./constants";
import { CACHE_KEYS } from "./commonHelpers";

interface fetchCacheUtilProps {
  url: string;
  cacheKey: CACHE_KEYS;
  ctype: string;
  useToken: boolean;
  useHeaders?: boolean;
  token?: string;
}

const fetchUtil = async (
  url: string,
  method: string,
  body: object,
  ctype: string,
  useToken: boolean,
  useHeaders?: string,
  token?: string
) => {
  let options = {};
  if (method === "GET") {
    options = useHeaders
      ? { method }
      : {
          method,
          headers: requestHeaders(ctype, useToken, token),
        };
  } else {
    options = {
      method,
      headers: requestHeaders(ctype, useToken, token),
      body: JSON.stringify(body),
    };
  }
  const res = await fetch(url, options);
  return await parseStatus(res.status, res);
};

const readFromLocalStore = (cacheKey: string) => {
  return JSON.parse(localStorage.getItem(cacheKey));
};

const isCacheDataExpired = (date: string) => {
  return compareAsc(new Date(date), new Date()) === -1 ? true : false;
};

export const fetchCacheUtil = async ({
  url,
  cacheKey,
  ctype,
  useToken,
  token,
  useHeaders,
}: fetchCacheUtilProps): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    const useCityId = [
      CACHE_KEYS.GET_WIDGETS,
      CACHE_KEYS.GET_WIDGETS_CART,
      CACHE_KEYS.GET_WIDGETS_DETAILS,
      CACHE_KEYS.SEARCH_POPULAR_ITEMS,
    ].includes(cacheKey);
    const urlParams = new URLSearchParams(`?${url.split("?")?.[1]}`);
    const cityId = urlParams.get("city") || null;
    const cacheData = readFromLocalStore(cacheKey);
    const cacheDataByURL = cacheData?.[useCityId ? cityId : url];
    if (cacheDataByURL && !isCacheDataExpired(cacheDataByURL?.expireDate)) {
      resolve(cacheDataByURL?.data);
    } else {
      let options = {};
      options = useHeaders
        ? { method: "GET" }
        : {
            method: "GET",
            headers: requestHeaders(ctype, useToken, token),
          };
      const res = await fetch(url, options);
      let data = await parseStatus(res.status, res);
      if (
        Array.isArray(data?.data) ||
        Array.isArray(data?.data?.diagnosticWidgetData) ||
        Array.isArray(data?.topItemDetails) ||
        data?.paymentText
      ) {
        const payload = useCityId
          ? {
              [cityId]: {
                data,
                expireDate: addMinutes(new Date(), CACHE_EXPIRE_IN_MINUTES),
              },
            }
          : {
              ...(cacheData ?? {}),
              [url]: {
                data,
                expireDate: addMinutes(new Date(), CACHE_EXPIRE_IN_MINUTES),
              },
            };
        try {
          localStorage.setItem(cacheKey, JSON.stringify(payload));
        } catch (e) {
          handleQuotaExceededError(e);
        }
        resolve(data);
      } else {
        reject(data);
      }
    }
  });
};

const handleQuotaExceededError = (err) => {
  // If localStorage quota exceeds, clear data
  if (err?.name === "QuotaExceededError") {
    Object.keys(localStorage)
      ?.filter(
        (x) =>
          x.includes("WIDGET_DATA") ||
          x.includes("CATEGORY_LISTING_DATA") ||
          x.includes("POPULAR_TEST_SEARCH_DATA")
      )
      ?.forEach((x) => localStorage.removeItem(x));
  } else {
    console.error(err);
  }
};

const parseStatus = (status: any, res: any): any => {
  return new Promise((resolve, reject) => {
    if (status >= 200 && status < 300) {
      if (res.url.indexOf(".html") > -1) {
        return resolve(res);
      }
      res.json().then((response: any) => resolve(response));
    } else {
      res.json().then((response: any) => reject(response));
    }
  });
};

const requestHeaders = (ctype: string, useToken: boolean, token?: string) => {
  return useToken
    ? {
        "Content-Type": ctype || "application/json",
        Authorization:
          token || "Basic Y29udGVudDp3YWxtYXJ0TlVUdG9reW9IZWlzdA==",
      }
    : {
        "Content-Type": ctype || "application/json",
      };
};

export default fetchUtil;
